import { useParams, useRouter } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";

import { useEffect, useState } from "react";
import { CurrencyInput } from "@/components/buy-log/CurrencyInput";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useProjectCostCodes } from "@/hooks/use-budgets";
import type { UpdateCommitmentPayload } from "@/hooks/use-commitments";
import {
  useCommitment,
  useUpdateCommitment,
  useUploadContractDocument,
} from "@/hooks/use-commitments";
import { useCompanies } from "@/hooks/use-companies";
import { toNumber } from "@/lib/formatters";
import type { DraftG703Line } from "@/routes/dashboard/commitment-new";
import { FileUploadField, freshLine, G703Table } from "@/routes/dashboard/commitment-new";

type CommitmentType =
  | "subcontract"
  | "purchase_order"
  | "prime_contract"
  | "change_order"
  | "owner_contract";
type CommitmentStatus =
  | "draft"
  | "pending_approval"
  | "pending_execution"
  | "executed"
  | "on_hold"
  | "closed"
  | "terminated"
  | "voided";

const COMMITMENT_TYPE_LABELS: Record<CommitmentType, string> = {
  subcontract: "Subcontract",
  purchase_order: "Purchase Order",
  prime_contract: "Prime Contract",
  change_order: "Change Order",
  owner_contract: "Owner Contract",
};

const STATUS_LABELS: Record<CommitmentStatus, string> = {
  draft: "Draft",
  pending_approval: "Pending Approval",
  pending_execution: "Pending Execution",
  executed: "Executed",
  on_hold: "On Hold",
  closed: "Closed",
  terminated: "Terminated",
  voided: "Voided",
};

export function CommitmentEditPage() {
  const rawParams = useParams({ strict: false });
  const router = useRouter();
  const commitmentId = typeof rawParams.id === "string" ? rawParams.id : "";

  const { data: commitment, isLoading, isError, error, refetch } = useCommitment(commitmentId);
  const projectId = commitment?.projectId ?? "";
  const costCodesQuery = useProjectCostCodes(projectId);
  const companies = useCompanies();
  const updateMutation = useUpdateCommitment();
  const uploadMutation = useUploadContractDocument();

  const [initialised, setInitialised] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [commitmentType, setCommitmentType] = useState<CommitmentType>("subcontract");
  const [status, setStatus] = useState<CommitmentStatus>("draft");
  const [companyId, setCompanyId] = useState("");
  const [originalAmount, setOriginalAmount] = useState("");
  const [retentionPercent, setRetentionPercent] = useState("10.00");
  const [startDate, setStartDate] = useState("");
  const [scheduledEndDate, setScheduledEndDate] = useState("");
  const [contractFile, setContractFile] = useState<File | null>(null);
  const [lines, setLines] = useState<DraftG703Line[]>([]);

  useEffect(() => {
    if (!commitment || initialised) return;
    setTitle(commitment.title ?? "");
    setDescription(commitment.description ?? "");
    setCommitmentType((commitment.commitmentType as CommitmentType) ?? "subcontract");
    setStatus((commitment.status as CommitmentStatus) ?? "draft");
    setCompanyId(commitment.companyId ?? "");
    setOriginalAmount(commitment.originalAmount ?? "");
    setRetentionPercent(commitment.retentionPercent ?? "10.00");
    setStartDate(commitment.startDate ?? "");
    setScheduledEndDate(commitment.scheduledEndDate ?? "");

    const defaultCostCodeId = costCodesQuery.data?.[0]?.id ?? "";
    const existingLines = (commitment.lineItems ?? []).map((li) => ({
      localId: crypto.randomUUID(),
      itemNo: li.itemNo,
      description: li.description,
      scheduledValue: li.scheduledValue,
      costCodeId: li.costCodeId ?? defaultCostCodeId,
    }));
    setLines(existingLines.length > 0 ? existingLines : [freshLine(1, defaultCostCodeId)]);
    setInitialised(true);
  }, [commitment, costCodesQuery.data, initialised]);

  const subcontractorCompanies = (companies.data ?? []).filter((c) => {
    if (!c.type) return true;
    const allowed = new Set([
      "subcontractor",
      "vendor",
      "contractor",
      "general_contractor",
      "material_supplier",
    ]);
    return allowed.has(c.type);
  });

  const linesValid =
    lines.length > 0 && lines.every((l) => l.description.trim() && l.itemNo.trim());
  const isPending = updateMutation.isPending || uploadMutation.isPending;

  function updateLine(localId: string, patch: Partial<DraftG703Line>) {
    setLines((prev) => prev.map((l) => (l.localId === localId ? { ...l, ...patch } : l)));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !linesValid) return;

    const hasG703 = lines.some((l) => toNumber(l.scheduledValue) > 0);

    const payload: UpdateCommitmentPayload = {
      id: commitmentId,
      title: title.trim(),
      description,
      commitmentType,
      status,
      companyId: companyId || null,
      toCompanyId: companyId || null,
      originalAmount: originalAmount || null,
      retentionPercent,
      startDate: startDate || undefined,
      scheduledEndDate: scheduledEndDate || undefined,
      lineItems: hasG703
        ? lines.map((l) => ({
            itemNo: l.itemNo,
            description: l.description,
            scheduledValue: l.scheduledValue === "" ? "0" : l.scheduledValue,
            costCodeId: l.costCodeId || null,
            isAllowance: false,
          }))
        : [],
    };

    updateMutation.mutate(payload, {
      onSuccess: async () => {
        if (contractFile) {
          try {
            await uploadMutation.mutateAsync({ id: commitmentId, file: contractFile });
          } catch {
            return;
          }
        }
        router.history.back();
      },
    });
  }

  if (!commitmentId) {
    return <ErrorState message="No commitment ID provided" />;
  }

  if (isLoading || (commitment && !initialised && costCodesQuery.isLoading)) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load commitment"}
        onRetry={() => refetch()}
      />
    );
  }

  if (!commitment) {
    return <ErrorState message="Commitment not found" />;
  }

  const costCodes = costCodesQuery.data ?? [];

  return (
    <div className="-m-6 flex h-[calc(100vh-97px)] flex-col">
      {/* Header */}
      <div className="shrink-0 border-b border-gray-200 bg-white px-6 py-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => router.history.back()}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">
              Edit Commitment
              {commitment.commitmentNumber ? ` — ${commitment.commitmentNumber}` : ""}
            </h1>
            <p className="text-sm text-gray-500">
              Update commitment details and schedule of values.
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
        <div className="flex-1 min-h-0 overflow-y-auto space-y-5 px-6 py-5">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
            <div className="col-span-2 space-y-1.5 md:col-span-3">
              <Label htmlFor="e-title">Title *</Label>
              <Input
                id="e-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Concrete subcontract"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="e-type">Commitment Type</Label>
              <Select
                value={commitmentType}
                onValueChange={(v) => setCommitmentType(v as CommitmentType)}
              >
                <SelectTrigger id="e-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(COMMITMENT_TYPE_LABELS) as CommitmentType[]).map((t) => (
                    <SelectItem key={t} value={t}>
                      {COMMITMENT_TYPE_LABELS[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="e-status">Status</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as CommitmentStatus)}>
                <SelectTrigger id="e-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(STATUS_LABELS) as CommitmentStatus[]).map((s) => (
                    <SelectItem key={s} value={s}>
                      {STATUS_LABELS[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="e-amount">Original Amount</Label>
              <CurrencyInput
                value={originalAmount}
                onCommit={(next) => setOriginalAmount(next)}
                ariaLabel="Original amount"
                className="w-full"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="e-retention">Retention %</Label>
              <Input
                id="e-retention"
                value={retentionPercent}
                onChange={(e) => setRetentionPercent(e.target.value)}
                placeholder="10.00"
                type="number"
                min="0"
                max="100"
                step="0.01"
              />
            </div>

            <div className="col-span-2 space-y-1.5 md:col-span-3">
              <Label htmlFor="e-company">Company</Label>
              <Select value={companyId} onValueChange={setCompanyId}>
                <SelectTrigger id="e-company">
                  <SelectValue placeholder="Select a company (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {subcontractorCompanies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.companyName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Start Date</Label>
              <DatePicker
                value={startDate || null}
                onChange={(next) => setStartDate(next ?? "")}
                ariaLabel="Start date"
                className="w-full"
              />
            </div>

            <div className="space-y-1.5">
              <Label>Scheduled End Date</Label>
              <DatePicker
                value={scheduledEndDate || null}
                onChange={(next) => setScheduledEndDate(next ?? "")}
                ariaLabel="Scheduled end date"
                className="w-full"
              />
            </div>

            <div className="col-span-2 space-y-1.5 md:col-span-3">
              <Label htmlFor="e-desc">Description</Label>
              <Textarea
                id="e-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description or scope notes"
                rows={3}
              />
            </div>
          </div>

          <FileUploadField
            file={contractFile}
            onSelect={setContractFile}
            onClear={() => setContractFile(null)}
          />

          {costCodes.length > 0 && (
            <G703Table
              lines={lines}
              costCodes={costCodes}
              defaultCostCodeId={costCodes[0]?.id ?? ""}
              totalAward={null}
              onUpdate={updateLine}
              onAdd={() =>
                setLines((prev) => [...prev, freshLine(prev.length + 1, costCodes[0]?.id ?? "")])
              }
              onRemove={(id) => setLines((prev) => prev.filter((l) => l.localId !== id))}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-gray-200 bg-white px-6 py-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => router.history.back()}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={!title.trim() || !linesValid || isPending}>
            {updateMutation.isPending
              ? "Saving..."
              : uploadMutation.isPending
                ? "Uploading..."
                : "Save Changes"}
          </Button>
        </div>
      </form>
    </div>
  );
}
